import React, { useState } from 'react';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { useWorkspaceRoster } from '../../hooks/useWorkspaceRoster';
import projectManager from '../../models/projects';

// ── Shared field styles ───────────────────────────────────────────────────────
const fieldStyle = {
  width: '100%',
  padding: '0.75rem',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  fontSize: '1rem',            // ≥16px prevents iOS zoom
  color: '#111827',
  background: '#ffffff',
  boxSizing: 'border-box',
  fontFamily: 'Raleway, sans-serif',
  appearance: 'auto',
};

const labelStyle = {
  display: 'block',
  marginBottom: '0.4rem',
  fontWeight: 600,
  fontSize: '0.88rem',
  color: '#374151',
  fontFamily: 'Raleway, sans-serif',
};

function CreateTaskModal({ isOpen, onClose, onSave, projectId = null, sprintId = null }) {
  const { currentUser } = useCurrentUser();
  const isMobile = typeof window !== 'undefined'
    && (window.innerWidth <= 768 || window.matchMedia('(pointer: coarse)').matches);

  const [formData, setFormData] = useState({
    title:      '',
    description:'',
    projectId:  projectId || '',
    sprintId:   sprintId  || '',
    assignedTo: currentUser?.id ? [currentUser.id] : [],
    dueDate:    '',
    priority:   'Medium',
    status:     'todo',
    storyPoints: 5,
    tags:       [],
  });

  const [tagInput,        setTagInput]        = useState('');
  const [showNewProject,  setShowNewProject]  = useState(false);
  const [newProjectName,  setNewProjectName]  = useState('');
  const [creatingProject, setCreatingProject] = useState(false);

  const [projects, setProjects] = useState(() => projectManager.getAllProjects());
  const { members: users } = useWorkspaceRoster();
  const sprints = formData.projectId
    ? projectManager.getSprintsByProject(parseInt(formData.projectId))
    : [];

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const toggleAssignee = (userId) => {
    set('assignedTo',
      formData.assignedTo.includes(userId)
        ? formData.assignedTo.filter(id => id !== userId)
        : [...formData.assignedTo, userId]
    );
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      set('tags', [...formData.tags, tag]);
      setTagInput('');
    }
  };

  const removeTag = (tag) => set('tags', formData.tags.filter(t => t !== tag));

  // ── Create new project inline ─────────────────────────────────────────────

  const handleCreateProject = async () => {
    const name = newProjectName.trim();
    if (!name) return;
    setCreatingProject(true);
    try {
      const created = await projectManager.createProject({ name, color: '#5352ed', status: 'active' });
      const refreshed = projectManager.getAllProjects();
      setProjects(refreshed);
      const newId = created?.id || refreshed.find(p => p.name === name)?.id;
      if (newId) {
        set('projectId', String(newId));
        set('sprintId', '');
      }
    } catch (err) {
      console.error('Failed to create project:', err);
    } finally {
      setCreatingProject(false);
      setShowNewProject(false);
      setNewProjectName('');
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) { alert('Please enter a task title'); return; }
    if (!formData.projectId)    { alert('Please select a project');    return; }

    onSave({
      ...formData,
      projectId:   parseInt(formData.projectId),
      sprintId:    formData.sprintId ? parseInt(formData.sprintId) : null,
      storyPoints: parseInt(formData.storyPoints),
      createdBy:   currentUser?.id || null,
    });
    onClose();

    setFormData({
      title: '', description: '', projectId: projectId || '',
      sprintId: sprintId || '', assignedTo: currentUser?.id ? [currentUser.id] : [],
      dueDate: '', priority: 'Medium', status: 'todo', storyPoints: 5, tags: [],
    });
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: isMobile ? 'flex-end' : 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: isMobile ? 0 : '1rem',
    }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: isMobile ? '16px 16px 0 0' : '14px',
        padding: isMobile ? '1.25rem 1rem 2rem' : '1.75rem 2rem',
        width: '100%',
        maxWidth: isMobile ? '100%' : '580px',
        maxHeight: isMobile ? '92vh' : '90vh',
        overflowY: 'auto',
        boxSizing: 'border-box',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: '#0C2D48', fontFamily: 'Poppins, sans-serif' }}>
            Create New Task
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>

          {/* Title */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Task Title *</label>
            <input
              type="text" value={formData.title} required
              onChange={e => set('title', e.target.value)}
              placeholder="Enter task title…"
              style={fieldStyle}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Description</label>
            <textarea
              value={formData.description}
              onChange={e => set('description', e.target.value)}
              placeholder="Describe the task…"
              style={{ ...fieldStyle, minHeight: '72px', resize: 'vertical' }}
            />
          </div>

          {/* Project + Sprint */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.85rem', marginBottom: '1rem' }}>
            <div>
              <label style={labelStyle}>Project *</label>
              <select
                value={formData.projectId}
                onChange={e => {
                  if (e.target.value === '__new__') {
                    setShowNewProject(true);
                  } else {
                    set('projectId', e.target.value);
                    set('sprintId', '');
                    setShowNewProject(false);
                  }
                }}
                style={fieldStyle}
                required
              >
                <option value="">Select project…</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
                <option value="__new__">＋ Create new project…</option>
              </select>

              {/* Inline new project form */}
              {showNewProject && (
                <div style={{
                  marginTop: '0.5rem', padding: '0.75rem',
                  background: '#f8f9fb', borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                }}>
                  <input
                    autoFocus
                    type="text"
                    value={newProjectName}
                    onChange={e => setNewProjectName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleCreateProject(); } if (e.key === 'Escape') { setShowNewProject(false); set('projectId', ''); }}}
                    placeholder="Project name…"
                    style={{ ...fieldStyle, marginBottom: '0.5rem' }}
                  />
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={handleCreateProject}
                      disabled={!newProjectName.trim() || creatingProject}
                      style={{
                        flex: 1, padding: '0.5rem', borderRadius: '6px',
                        border: 'none', background: '#0C2D48', color: '#fff',
                        fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
                        fontFamily: 'Raleway, sans-serif',
                      }}
                    >
                      {creatingProject ? 'Creating…' : 'Create'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowNewProject(false); set('projectId', ''); setNewProjectName(''); }}
                      style={{
                        padding: '0.5rem 0.75rem', borderRadius: '6px',
                        border: '1px solid #e2e8f0', background: '#fff',
                        fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'Raleway, sans-serif',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label style={labelStyle}>Sprint (optional)</label>
              <select
                value={formData.sprintId}
                onChange={e => set('sprintId', e.target.value)}
                disabled={!formData.projectId || showNewProject}
                style={{ ...fieldStyle, opacity: !formData.projectId || showNewProject ? 0.6 : 1 }}
              >
                <option value="">No sprint</option>
                {sprints.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Assigned to */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Assigned To</label>
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: '0.5rem',
              padding: '0.65rem', border: '1px solid #d1d5db',
              borderRadius: '8px', background: '#f9fafb',
            }}>
              {users.map(user => (
                <label
                  key={user.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                    padding: '0.4rem 0.65rem', borderRadius: '20px', cursor: 'pointer',
                    background: formData.assignedTo.includes(user.id) ? '#E4E69B' : '#fff',
                    border: `1px solid ${formData.assignedTo.includes(user.id) ? '#c8ca7d' : '#e2e8f0'}`,
                    fontFamily: 'Raleway, sans-serif', fontSize: '0.85rem',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="checkbox" style={{ display: 'none' }}
                    checked={formData.assignedTo.includes(user.id)}
                    onChange={() => toggleAssignee(user.id)}
                  />
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%',
                    background: user.color || '#5352ed', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                  }}>
                    {(user.name || '?')[0]}
                  </div>
                  <span style={{ color: '#111827', fontWeight: formData.assignedTo.includes(user.id) ? 700 : 400 }}>
                    {user.name}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Due date + Priority + Story points */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: '0.85rem', marginBottom: '1rem' }}>
            <div style={{ gridColumn: isMobile ? '1 / -1' : 'auto' }}>
              <label style={labelStyle}>Due Date</label>
              <input
                type="date" value={formData.dueDate}
                onChange={e => set('dueDate', e.target.value)}
                style={fieldStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Priority</label>
              <select
                value={formData.priority}
                onChange={e => set('priority', e.target.value)}
                style={fieldStyle}
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Story Points</label>
              <input
                type="number" min="1" max="50" value={formData.storyPoints}
                onChange={e => set('storyPoints', e.target.value)}
                style={fieldStyle}
              />
            </div>
          </div>

          {/* Tags */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={labelStyle}>Tags</label>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <input
                type="text" value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="Add a tag…"
                style={{ ...fieldStyle, flex: 1 }}
              />
              <button
                type="button" onClick={addTag}
                style={{
                  padding: '0 1rem', borderRadius: '8px',
                  border: '1px solid #d1d5db', background: '#f9fafb',
                  cursor: 'pointer', fontSize: '0.9rem', fontFamily: 'Raleway, sans-serif',
                  color: '#374151', whiteSpace: 'nowrap',
                }}
              >
                Add
              </button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
              {formData.tags.map(tag => (
                <span key={tag} style={{
                  background: '#e5e7eb', color: '#374151',
                  padding: '0.25rem 0.6rem', borderRadius: '20px',
                  fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem',
                }}>
                  {tag}
                  <button
                    type="button" onClick={() => removeTag(tag)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '0.85rem', padding: 0, lineHeight: 1 }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '0.75rem', flexDirection: isMobile ? 'column-reverse' : 'row', justifyContent: 'flex-end' }}>
            <button
              type="button" onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem', borderRadius: '8px',
                border: '1px solid #d1d5db', background: '#fff',
                cursor: 'pointer', fontSize: '0.95rem',
                fontFamily: 'Raleway, sans-serif', color: '#374151',
                width: isMobile ? '100%' : 'auto',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '0.75rem 1.5rem', borderRadius: '8px',
                border: 'none', background: '#5352ed', color: '#fff',
                cursor: 'pointer', fontSize: '0.95rem', fontWeight: 700,
                fontFamily: 'Raleway, sans-serif',
                width: isMobile ? '100%' : 'auto',
              }}
            >
              Create Task
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateTaskModal;
